import { useContext } from 'react';
import { ChefHat } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useKitchenReadyNotifications } from '@/hooks/kitchen/useKitchenReadyNotifications';
import { getTimeBadgeClass, useElapsedMinutes } from '@/components/kitchen/kitchenTime';
import { isItemActive, type KitchenTicket } from '@/types/kitchen';
import { cn } from '@/lib/utils';

const readyReferenceTime = (ticket: KitchenTicket) => {
  const itemReadyAt = ticket.items.find(
    (item) => isItemActive(item) && item.status === 'ready' && item.readyAt,
  )?.readyAt;
  return itemReadyAt ?? ticket.readyAt ?? ticket.createdAt;
};

const ReadyTicketRow = ({ ticket }: { ticket: KitchenTicket }) => {
  const { translator } = useContext(LanguageProviderContext);
  const elapsedMinutes = useElapsedMinutes(readyReferenceTime(ticket));

  return (
    <div className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-2">
      <div className="min-w-0">
        <p className="truncate font-semibold tabular-nums">
          {translator('Kitchen.order')}: #{ticket.tabId.slice(-3).toUpperCase()}
        </p>
        <p className="truncate text-sm text-muted-foreground">{ticket.customerName}</p>
      </div>
      <Badge variant="outline" className={cn('shrink-0 tabular-nums', getTimeBadgeClass(elapsedMinutes))}>
        {translator('Kitchen.elapsedMinutes', { minutes: String(elapsedMinutes) })}
      </Badge>
    </div>
  );
};

export const KitchenReadyNotificationsButton = () => {
  const { translator } = useContext(LanguageProviderContext);
  const { isFeatureEnabled, readyTickets, readyCount } = useKitchenReadyNotifications();

  if (!isFeatureEnabled) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="default"
          className="relative h-10 touch-manipulation px-3"
          aria-label={translator('Kitchen.readyOrders')}
        >
          <ChefHat className="h-4 w-4" />
          {readyCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center p-0 px-1 text-xs"
            >
              {readyCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end">
        <p className="mb-2 px-2 text-sm font-semibold">{translator('Kitchen.readyOrders')}</p>
        {readyTickets.length === 0 ? (
          <p className="px-2 py-4 text-sm text-muted-foreground">{translator('Kitchen.readyEmpty')}</p>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {readyTickets.map((ticket) => (
              <ReadyTicketRow key={ticket.id} ticket={ticket} />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
